import type {
  BookShelfConfig,
  Symbol3DMetadata,
} from "../../../../types/floorplannerTypes";

export const BOOK_SHELF_CONFIG_VERSION = 1 as const;
export const BOOK_SHELF_LIBRARY_KEY = "MANUAL/bookshelf";
export const BOOK_SHELF_MODEL_KEY = "living:bookshelf";

export const BOOK_SHELF_DEFAULT_WIDTH_M = 0.8;
export const BOOK_SHELF_DEFAULT_DEPTH_M = 0.3;
export const BOOK_SHELF_DEFAULT_SHELF_COUNT = 5;
export const BOOK_SHELF_DEFAULT_SHELF_SPACING_M = 0.32;
export const BOOK_SHELF_DEFAULT_HAS_FRAME = true;
export const BOOK_SHELF_DEFAULT_HAS_BACK_PANEL = true;

export const BOOK_SHELF_MIN_WIDTH_M = 0.3;
export const BOOK_SHELF_MAX_WIDTH_M = 4;
export const BOOK_SHELF_MIN_DEPTH_M = 0.15;
export const BOOK_SHELF_MAX_DEPTH_M = 0.8;
export const BOOK_SHELF_MIN_SHELF_COUNT = 2;
export const BOOK_SHELF_MAX_SHELF_COUNT = 20;
export const BOOK_SHELF_MIN_SHELF_SPACING_M = 0.12;
export const BOOK_SHELF_MAX_SHELF_SPACING_M = 0.8;
export const BOOK_SHELF_MIN_HEIGHT_M = 0.4;
export const BOOK_SHELF_MAX_HEIGHT_M = 3.5;

/** Horizontal shelf board thickness in metres. */
export const BOOK_SHELF_BOARD_THICKNESS_M = 0.018;
/** Side panel thickness in metres. */
export const BOOK_SHELF_SIDE_THICKNESS_M = 0.018;
/** Back panel thickness in metres. */
export const BOOK_SHELF_BACK_THICKNESS_M = 0.008;

const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Overall unit height from shelf board count and clear spacing between boards.
 * Height = boards * thickness + gaps * spacing.
 */
export function deriveBookShelfHeight(
  shelfCount: number,
  shelfSpacing: number,
): number {
  const boards = clamp(
    Math.round(shelfCount),
    BOOK_SHELF_MIN_SHELF_COUNT,
    BOOK_SHELF_MAX_SHELF_COUNT,
  );
  const spacing = clamp(
    shelfSpacing,
    BOOK_SHELF_MIN_SHELF_SPACING_M,
    BOOK_SHELF_MAX_SHELF_SPACING_M,
  );
  return (
    boards * BOOK_SHELF_BOARD_THICKNESS_M + (boards - 1) * spacing
  );
}

/**
 * Clear spacing implied by an overall height and shelf board count.
 */
export function deriveBookShelfSpacingFromHeight(
  height: number,
  shelfCount: number,
): number {
  const boards = clamp(
    Math.round(shelfCount),
    BOOK_SHELF_MIN_SHELF_COUNT,
    BOOK_SHELF_MAX_SHELF_COUNT,
  );
  const usable = Math.max(
    height - boards * BOOK_SHELF_BOARD_THICKNESS_M,
    BOOK_SHELF_MIN_SHELF_SPACING_M * (boards - 1),
  );
  return clamp(
    usable / (boards - 1),
    BOOK_SHELF_MIN_SHELF_SPACING_M,
    BOOK_SHELF_MAX_SHELF_SPACING_M,
  );
}

export function createDefaultBookShelfConfig(): BookShelfConfig {
  const shelfCount = BOOK_SHELF_DEFAULT_SHELF_COUNT;
  const shelfSpacing = BOOK_SHELF_DEFAULT_SHELF_SPACING_M;
  return {
    version: BOOK_SHELF_CONFIG_VERSION,
    width: BOOK_SHELF_DEFAULT_WIDTH_M,
    depth: BOOK_SHELF_DEFAULT_DEPTH_M,
    height: deriveBookShelfHeight(shelfCount, shelfSpacing),
    shelfCount,
    shelfSpacing,
    hasFrame: BOOK_SHELF_DEFAULT_HAS_FRAME,
    hasBackPanel: BOOK_SHELF_DEFAULT_HAS_BACK_PANEL,
  };
}

export function createBookShelf3DMetadata(): Symbol3DMetadata {
  const defaults = createDefaultBookShelfConfig();
  return {
    sourceMode: "primitiveOnly",
    modelKey: BOOK_SHELF_MODEL_KEY,
    height: defaults.height,
    baseElevation: 0,
    collision: "box",
    dimensions: {
      width: defaults.width,
      depth: defaults.depth,
      height: defaults.height,
    },
  };
}

/**
 * Normalize persisted / UI bookshelf config. Spacing drives height unless only
 * height is provided (then spacing is derived from height + shelf count).
 */
export function normalizeBookShelfConfig(value: unknown): BookShelfConfig {
  const raw = value && typeof value === "object" ? (value as any) : {};
  const defaults = createDefaultBookShelfConfig();

  const width = clamp(
    finiteOr(raw.width, defaults.width),
    BOOK_SHELF_MIN_WIDTH_M,
    BOOK_SHELF_MAX_WIDTH_M,
  );
  const depth = clamp(
    finiteOr(raw.depth, defaults.depth),
    BOOK_SHELF_MIN_DEPTH_M,
    BOOK_SHELF_MAX_DEPTH_M,
  );
  const shelfCount = clamp(
    Math.round(finiteOr(raw.shelfCount, defaults.shelfCount)),
    BOOK_SHELF_MIN_SHELF_COUNT,
    BOOK_SHELF_MAX_SHELF_COUNT,
  );
  const hasFrame = raw.hasFrame !== false;
  const hasBackPanel = raw.hasBackPanel !== false;

  const hasExplicitSpacing =
    typeof raw.shelfSpacing === "number" && Number.isFinite(raw.shelfSpacing);
  const hasExplicitHeight =
    typeof raw.height === "number" && Number.isFinite(raw.height);

  // Spacing is the primary vertical control. Height alone implies spacing.
  const shelfSpacing = hasExplicitSpacing
    ? clamp(
        raw.shelfSpacing,
        BOOK_SHELF_MIN_SHELF_SPACING_M,
        BOOK_SHELF_MAX_SHELF_SPACING_M,
      )
    : hasExplicitHeight
      ? deriveBookShelfSpacingFromHeight(raw.height, shelfCount)
      : defaults.shelfSpacing;

  const height = clamp(
    deriveBookShelfHeight(shelfCount, shelfSpacing),
    BOOK_SHELF_MIN_HEIGHT_M,
    BOOK_SHELF_MAX_HEIGHT_M,
  );

  return {
    version: BOOK_SHELF_CONFIG_VERSION,
    width,
    depth,
    height,
    shelfCount,
    shelfSpacing,
    hasFrame,
    hasBackPanel,
  };
}

export function normalizeBookShelfHeight(value: unknown): number {
  const defaults = createDefaultBookShelfConfig();
  return clamp(
    finiteOr(value, defaults.height),
    BOOK_SHELF_MIN_HEIGHT_M,
    BOOK_SHELF_MAX_HEIGHT_M,
  );
}
